import type { CatalogDocumentDTO } from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { ReferenceStatusBadge } from './ReferenceStatusBadge'
import {
  formatDate,
  referenceSubtitle,
  referenceTitle,
  referenceTypeLabel,
} from './referenceUtils'

interface Props {
  doc: CatalogDocumentDTO
  /** Opens the parent's delete confirmation modal for this card. */
  onRequestDelete: () => void
  /** True while a DELETE for this card is in-flight. */
  isDeleting?: boolean
}

/**
 * Glass card for a Reference Library document. Visually consistent with
 * ``DocumentCard`` (case documents) — the difference is the metadata grid
 * shown: reference_type / domain / year / source / title instead of
 * entity/group coordinates. There is no "Usar no chat" CTA yet because
 * multi-context RAG is deferred to a future step.
 */
export function ReferenceDocumentCard({
  doc,
  onRequestDelete,
  isDeleting = false,
}: Props) {
  const title = referenceTitle(doc)
  const subtitle = referenceSubtitle(doc)
  const indexedAtFmt = formatDate(doc.indexed_at)
  const createdAtFmt = formatDate(doc.created_at)
  const showCreatedRow = createdAtFmt !== '—' && createdAtFmt !== indexedAtFmt

  return (
    <article
      className={`doc-card${isDeleting ? ' is-deleting' : ''}`}
      data-testid="reference-card"
      data-document-id={doc.document_id}
    >
      <header className="doc-card-head">
        <div className="doc-card-icon" aria-hidden="true">
          <Icon name="doc" />
        </div>
        <div className="doc-card-titles">
          <h3 className="doc-card-title" title={title}>
            {title}
          </h3>
          {subtitle && (
            <div className="doc-card-subtitle">{subtitle}</div>
          )}
        </div>
        <ReferenceStatusBadge status={doc.status} />
        <button
          type="button"
          className="doc-card-delete"
          data-testid="reference-delete"
          onClick={onRequestDelete}
          disabled={isDeleting}
          aria-label={`Excluir referência ${title}`}
          title="Excluir referência"
        >
          <Icon name="trash" />
        </button>
      </header>

      <dl className="doc-card-grid">
        <div>
          <dt>document_id</dt>
          <dd className="mono" title={doc.document_id}>
            {doc.document_id}
          </dd>
        </div>
        <div>
          <dt>Chunks</dt>
          <dd className={doc.chunk_count <= 0 ? 'is-warning' : ''}>
            {doc.chunk_count}
          </dd>
        </div>
        <div>
          <dt>Tipo</dt>
          <dd>{referenceTypeLabel(doc.reference_type)}</dd>
        </div>
        {doc.reference_domain ? (
          <div>
            <dt>Domínio</dt>
            <dd>{doc.reference_domain}</dd>
          </div>
        ) : null}
        {doc.reference_year != null ? (
          <div>
            <dt>Ano</dt>
            <dd>{doc.reference_year}</dd>
          </div>
        ) : null}
        {doc.reference_source ? (
          <div>
            <dt>Fonte</dt>
            <dd title={doc.reference_source}>{doc.reference_source}</dd>
          </div>
        ) : null}
        <div>
          <dt>Embedding</dt>
          <dd className="mono" title={doc.embedding_model ?? ''}>
            {doc.embedding_model ?? '—'}
          </dd>
        </div>
        <div>
          <dt>Indexado em</dt>
          <dd>{indexedAtFmt}</dd>
        </div>
        {showCreatedRow ? (
          <div>
            <dt>Criado em</dt>
            <dd>{createdAtFmt}</dd>
          </div>
        ) : null}
      </dl>
    </article>
  )
}
